/**
 * How a refused write is shown (ORDR-119, seam C1 of w2c-ord-config).
 *
 * BRANCHES ON `extensions.check` AND `extensions.reason` ONLY, NEVER ON
 * `detail`. The detail is prose the server may reword; the reason is the
 * contract. `detail` is displayed where the table says to, never parsed.
 *
 * Anything unrecognised falls back to "Could not save" with the correlation id,
 * and the caller KEEPS the edit, so nothing typed is lost to a refusal nobody
 * anticipated.
 */

import type { Failure, WriteOutcome } from "./configApi.js";
import type { ConfigDomain } from "./domains.js";

export interface RefusalView {
  readonly message: string;
  /** The key the message belongs beside, when the server named one. */
  readonly field: string | null;
  /** The server says nobody may edit this group: take the control away. */
  readonly removeControl: boolean;
  /** The tree changed under the editor: re-read before anything else. */
  readonly reload: boolean;
}

function text(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

export function describeRefusal(
  outcome: Extract<WriteOutcome, { ok: false }>,
  domain: string,
): RefusalView {
  const view = (message: string, over: Partial<RefusalView> = {}): RefusalView => ({
    message, field: null, removeControl: false, reload: false, ...over,
  });
  const fallback = view(`Could not save. Reference: ${outcome.correlationId}.`);
  const ext = outcome.extensions;
  if (ext === null) {
    return fallback;
  }
  const detail = outcome.detail ?? fallback.message;

  switch (`${String(ext.check)}/${String(ext.reason)}`) {
    case "AUTHORITY/READ_ONLY_DOMAIN":
      return view("This group is read-only.", { removeControl: true });
    case "AUTHORITY/CAPABILITY_NOT_HELD":
      return view(`You need ${text(ext.required_capability) ?? "a capability you do not hold"} here.`);
    case "AUTHORITY/LOCKED_ABOVE":
      return view(detail, { reload: true });
    case "NODE_LEVEL/NODE_LEVEL_NOT_PERMITTED": {
      const types = Array.isArray(ext.permitted_node_types) ? ext.permitted_node_types.map(String) : [];
      // ORDR-175: a node's type is fixed, so no edit here will ever be
      // accepted; the group goes read-only with this sentence, as
      // READ_ONLY_DOMAIN does. The ordering context carries no node type, so
      // this refusal is the first place the client learns the level.
      return view(`${domain} can only be set at: ${types.join(", ")}.`, { removeControl: true });
    }
    case "CONSTRAINT/UNKNOWN_KEY":
    case "CONSTRAINT/INVALID_VALUE":
      return view(detail, { field: text(ext.key) });
    case "CONSTRAINT/PAYLOAD_NOT_OBJECT":
      return view("Settings must be an object.");
    default:
      return fallback;
  }
}

/** A failed GET, said plainly, with the reference support needs (item 8). */
export function failureText(domain: ConfigDomain, failure: Failure): string {
  const reference = `Reference: ${failure.correlationId}.`;
  if (failure.status === 429) {
    return `Too many requests; try again in a minute. ${reference}`;
  }
  const drift = failure.status === 400 ? " (the service does not know this group)" : "";
  return `Could not load ${domain}. ${reference}${drift}`;
}
